import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { LEASE_LABEL, leasePill, pill, unitState, LINK, MUTED, GHOST, fmtYmd, fmtTs, UnitForm, makeModel, readJson, JSON_HEADERS, downloadBlob } from '../lib/equipment'; // @@ASSET_LABEL_v103@@

// @@EQUIPMENT_LEASES_v87@@ HQ › Equipment › one unit: edit + lease history.
export default function EquipmentUnit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManage = !!user && ['admin', 'manager'].includes(user.role);
  const [unit, setUnit] = useState(null);
  const [form, setForm] = useState(null);
  const [leases, setLeases] = useState([]);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  async function load() {
    const r = await apiFetch('/api/equipment/units/' + id);
    const d = await readJson(r);
    if (!r.ok) { setMsg(d.error || 'Could not load this unit.'); return; }
    setUnit(d.unit); setLeases(d.leases || []);
    setForm({ ...d.unit, replacement_value: d.unit.replacement_value ?? '' });
  }
  useEffect(() => { load(); }, [id]);

  async function save(e) {
    e.preventDefault();
    setBusy(true); setMsg('');
    const r = await apiFetch('/api/equipment/units/' + id, { method: 'PUT', headers: JSON_HEADERS, body: JSON.stringify(form) });
    const d = await readJson(r);
    setBusy(false);
    if (!r.ok) { setMsg(d.error || 'Could not save.'); return; }
    setUnit(d.unit); setForm({ ...d.unit, replacement_value: d.unit.replacement_value ?? '' }); setMsg('Saved.');
  }
  async function remove() {
    if (!window.confirm(`Delete ${unit.asset_tag} from inventory? This can’t be undone.`)) return;
    const r = await apiFetch('/api/equipment/units/' + id, { method: 'DELETE' });
    const d = await readJson(r);
    if (r.ok) navigate('/equipment?tab=inventory'); else setMsg(d.error || 'Could not delete.');
  }

  // @@ASSET_LABEL_v103@@ 4x6 asset label PDF (blob download keeps the Bearer header).
  // @@ASSET_LABEL_v103_1@@ layout: '1up' (with stub) | '2up' | '4up' — all on one 4x6 sheet.
  async function downloadLabel(layout = '1up') {
    const base = String(unit.asset_tag || 'asset').replace(/[^A-Za-z0-9._-]/g, '_');
    const name = base + (layout === '1up' ? '-label.pdf' : `-label-${layout}.pdf`);
    if (!(await downloadBlob(apiFetch, `/api/equipment/units/${id}/label.pdf?layout=${layout}`, name))) setMsg('Could not build the label.');
  }

  if (!unit || !form) {
    return <div className="hq-page-content"><p className="hq-msg">{msg || 'Loading…'}</p></div>;
  }
  const st = unitState(unit);
  return (
    <div className="hq-page-content">
      <div className="hq-page-head">
        <p style={{ marginBottom: 8 }}><Link to="/equipment?tab=inventory" style={LINK}>← Inventory</Link></p>
        <h1 style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>{unit.asset_tag} <span style={pill(st.tone)}>{st.label}</span></h1>
        <p>{makeModel(unit.make, unit.model) || 'No model set'}{unit.lease && <> · on <Link to={'/equipment/leases/' + unit.lease.id} style={LINK}>{unit.lease.number}</Link> ({unit.lease.client_name})</>}</p>
        <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={MUTED}>4×6 labels:</span>
          <button type="button" className="hq-signout" style={GHOST} onClick={() => downloadLabel('1up')} title="One label with a 1 in detachable stub">1-up + stub</button>
          <button type="button" className="hq-signout" style={GHOST} onClick={() => downloadLabel('2up')} title="Two 4×3 in labels on one 4×6 sheet">2-up (4×3)</button>
          <button type="button" className="hq-signout" style={GHOST} onClick={() => downloadLabel('4up')} title="Four 2×3 in labels on one 4×6 sheet">4-up (2×3)</button>
          {String(unit.asset_tag || '').length > 12 && <span style={MUTED}>Tag is long: the 4-up barcode gets very fine. Use 2-up if it won’t scan.</span>}
        </div>
      </div>
      {msg && <p className="hq-msg" style={{ marginTop: -12, marginBottom: 16 }}>{msg}</p>}
      <form className="hq-panel" style={{ marginBottom: 20 }} onSubmit={save}>
        <div className="hq-panel-head"><h2>Details</h2></div>
        <div style={{ padding: 20 }}>
          <UnitForm value={form} onChange={setForm} disabled={!canManage} />
          {canManage && (
            <div style={{ display: 'flex', gap: 10, marginTop: 18, flexWrap: 'wrap' }}>
              <button className="hq-btn" type="submit" disabled={busy || !String(form.asset_tag || '').trim()}>Save</button>
              {leases.length === 0 && <button type="button" className="hq-signout" style={GHOST} onClick={remove}>Delete unit</button>}
            </div>
          )}
          {canManage && leases.length > 0 && <p style={{ ...MUTED, marginTop: 12 }}>This unit has lease history, so it can’t be deleted. Set its status to Retired instead.</p>}
        </div>
      </form>
      <div className="hq-panel">
        <div className="hq-panel-head"><h2>Lease history</h2></div>
        <div style={{ overflowX: 'auto' }}>
          <table className="hq-table" style={{ minWidth: 640 }}>
            <thead><tr><th>Lease</th><th>Client</th><th>Rental period</th><th>Lease status</th><th>Unit came back</th></tr></thead>
            <tbody>
              {leases.map((l) => (
                <tr key={l.id + (l.returned_at || '')}>
                  <td><Link to={'/equipment/leases/' + l.id} style={LINK}>{l.number}</Link></td>
                  <td>{l.client_name}</td>
                  <td style={{ fontSize: 13 }}>{fmtYmd(l.start_date)} – {fmtYmd(l.end_date)}</td>
                  <td><span style={leasePill(l.status)}>{LEASE_LABEL[l.status] || l.status}</span></td>
                  <td style={{ fontSize: 13 }}>{l.return_condition ? `${l.return_condition === 'lost' ? 'Lost' : fmtTs(l.returned_at) + ' · ' + l.return_condition}${l.return_note ? ' — ' + l.return_note : ''}` : l.released_at ? 'Released (lease cancelled)' : '—'}</td>
                </tr>
              ))}
              {leases.length === 0 && <tr><td className="hq-empty" colSpan={5}>Never leased.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
